import SoundManager from "../../../../../lib/html5-software-library/src/ranj/audio/SoundManager";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import Anchor from "../../../../../lib/html5-software-library/src/ranj/layout/Anchor";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import AssetLibrary from "../../../core/assetloading/AssetLibrary";
import SoundType from "../../../core/sound/util/SoundType";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import TextTypeWriterAnimation from "../../../core/ui/animation/typewriters/TextTypeWriterAnimation";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import DisplayObjectUtil from "../../util/DisplayObjectUtil";
import PromiseUtil from "../../util/PromiseUtil";
import MissionBriefingEvent from "../model/MissionBriefingEvent";

export default class MissionBriefingScreenMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {RevisableLanguagePack} languagePack 
     * @param {AssetLibrary} assetLibrary 
     * @param {SoundManager} soundManager 
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, languagePack, assetLibrary, soundManager) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._languagePack = languagePack;
        this._assetLibrary = assetLibrary;
        this._soundManager = soundManager;

        /** @type {boolean} */
        this._allowClose = this.getCustomData().allowClose;
        /** @type {string} */
        this._baseLpKey = this.getCustomData().baseLpKey;
        /** @type {string} */
        this._backgroundImageId = this.getCustomData().backgroundImageId;

        this._currentPage = 0;
        this._numberOfPages = this._countPages();

        this._currentTypeWriter = null;
    }

    async initialize() {
        super.initialize();

        this._initializeBackground();

        this._panel = this._target.panel;
        this._coach = this._target.coach;
        this._coach.initialX = this._coach.x;

        this._backButton = this.createBracketButton(this._panel.backButton, this._handleBackButton.bind(this), this._languagePack.getText(`missionBriefing.backButtonLabel`), SoundType.BRACKET_BUTTON_CLICK);
        this._continueButton = this.createBracketButton(this._panel.continueButton, this._handleContinueButton.bind(this), this._languagePack.getText(`missionBriefing.continueButtonLabel`), SoundType.BRACKET_BUTTON_CLICK);
        this._closeButton = this.createBracketButton(this._panel.closeButton, this._handleCloseButton.bind(this), '', SoundType.BRACKET_BUTTON_CLICK);

        this._closeButton.setVisible(this._allowClose);
        this._backButton.setEnabled(false);

        this._panel.contentTextField.text = "";

        this._updateNavigationButtons();
        
        this.preventClickThrough();

        // Start sequence.
        await this._animateIn(650); // with a delay to allow the screen to transition in first.
        this._showPage(0);
    }

    _handleBackButton() {
        if (this._currentPage <= 0) {
            return;
        }

        this._showPage(this._currentPage - 1);
    }

    _handleContinueButton() {
        if (this._currentPage >= this._numberOfPages - 1) {
            this._finish();
            return;
        }

        this._showPage(this._currentPage + 1);
    }

    _initializeBackground() {
        this._backgroundImageContainer = this._target.bgImageContainer;
        this._backgroundImageContainer.removeAllChildren();

        const backgroundImage = this._assetLibrary.getAsset(this._backgroundImageId);
        if (!backgroundImage) {
            console.warn(`Background image with id "${this._backgroundImageId}" not found.`);
            return;
        }
        const backgroundBitmap = new createjs.Bitmap(backgroundImage);
        DisplayObjectUtil.anchorDisplayObject(backgroundBitmap, Anchor.C);
        this._backgroundImageContainer.addChild(backgroundBitmap);
    }

    _countPages() {
        for (let i = 0; i < 100; i++) {
            const lpKey = this._getLpKeyForStep(i);
            const hasTextForStep = this._languagePack.hasText(lpKey);
            if (!hasTextForStep) {
                return i;
            }
        }

        throw new Error(`Pages out of range.`);
    }

    _showPage(pageIndex) {
        this._currentPage = pageIndex;

        // Show briefing text for the current page.
        const lpKey = this._getLpKeyForStep(pageIndex);
        const targetText = this._languagePack.getText(lpKey);
        this._typeWriteText(targetText);
        this._updateNavigationButtons();
    }

    _updateNavigationButtons() {
        // Update the continue button label.
        const buttonLabel = this._currentPage >= this._numberOfPages - 1 ? this._languagePack.getText(`missionBriefing.finishButtonLabel`) : this._languagePack.getText(`missionBriefing.continueButtonLabel`);
        this._continueButton.setLabel(buttonLabel);

        // Disable back button on the first page
        if (this._currentPage == 0) {
            this._backButton.setEnabled(false);
        } else {
            this._backButton.setEnabled(true);
        }

        // Hides back button if there is only one page.
        if (this._numberOfPages === 1) {
            this._backButton.setVisible(false);
        }
    }

    async _typeWriteText(text) {
        if (this._typewriteSoundId) {
            this._soundManager.stopSound(this._typewriteSoundId, 50);
        }

        setTimeout(() => {
            this._typewriteSoundId = this._soundManager.playSound(SoundType.TYPEWRITER_COACH, null, null, 0, 0, -1);
        }, 1);

        await new Promise(resolve => {
            this._panel.contentTextField.text = "";

            const duration = text.length * .005;
            this._currentTypeWriter?.stop();
            this._currentTypeWriter = TextTypeWriterAnimation.animate(this._panel.contentTextField, text, duration, 0, resolve);
        });
        this._soundManager.stopSound(this._typewriteSoundId, 50);
    }

    _handleCloseButton() {
        this._finish();
    }

    async _finish() {
        this.disableInteraction();
        this._soundManager.stopSound(this._typewriteSoundId, 450);
        await this._animateOut();
        this.dispatch(MissionBriefingEvent.FINISH);
    }

    async _animateIn(delay = 0) {
        this._coach.alpha = 0;

        this._panel.alpha = 0;
        this._panel.scale = .8;

        await PromiseUtil.delay(delay);

        this._soundManager.playSound(SoundType.COACH_SHOW);
        this._coach.gotoAndPlay(0);
        await createjs.Tween.get(this._coach)
            .to({
                alpha: 1,
            }, 450, createjs.Ease.backOut)
            .toPromise();

        this._soundManager.playSound(SoundType.COACH_CHAT_PANEL_SHOW);
        await createjs.Tween.get(this._panel)
            .to({
                alpha: 1,
                scale: 1,
            }, 450, createjs.Ease.backOut)
            .toPromise();
    }

    async _animateOut() {
        this._soundManager.playSound(SoundType.COACH_CHAT_PANEL_HIDE);
        await createjs.Tween.get(this._panel)
            .to({
                alpha: 0,
                scale: .8,
            }, 450, createjs.Ease.backIn)
            .toPromise();
        await PromiseUtil.delay(250);

        this._soundManager.playSound(SoundType.COACH_HIDE);
        await createjs.Tween.get(this._coach)
            .to({
                x: this._coach.initialX + 200,
                alpha: 0,
            }, 450, createjs.Ease.backIn)
            .toPromise();
    }

    _getLpKeyForStep(step) {
        return `${this._baseLpKey}.steps.step${step}`;
    }
}